import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { AlertCircle, Leaf, ChevronDown, ShieldAlert } from 'lucide-react';
import { SafeChoiceProfile } from '../types';
import { cn } from '../lib/utils';

const ALLERGY_LABELS: Record<string, Record<string, string>> = {
  'ua': {
    'Peanuts': 'Арахіс',
    'Tree Nuts': 'Горіхи',
    'Dairy': 'Молочні продукти',
    'Eggs': 'Яйця',
    'Gluten': 'Глютен',
    'Soy': 'Соя',
    'Fish': 'Риба',
    'Shellfish': 'Морепродукти',
  },
  'en-US': {
    'Peanuts': 'Peanuts',
    'Tree Nuts': 'Tree Nuts',
    'Dairy': 'Dairy',
    'Eggs': 'Eggs',
    'Gluten': 'Gluten',
    'Soy': 'Soy',
    'Fish': 'Fish',
    'Shellfish': 'Shellfish',
  }
};

const PREFERENCE_LABELS: Record<string, Record<string, string>> = {
  'ua': {
    'Vegan': 'Веганство',
    'Vegetarian': 'Вегетаріанство',
    'Keto': 'Кето',
    'Paleo': 'Палео',
    'Low-FODMAP': 'Low-FODMAP',
    'Clean Label': 'Чистий склад',
  },
  'en-US': {
    'Vegan': 'Vegan',
    'Vegetarian': 'Vegetarian',
    'Keto': 'Keto',
    'Paleo': 'Paleo',
    'Low-FODMAP': 'Low-FODMAP',
    'Clean Label': 'Clean Label',
  }
};

const UI_TEXTS = {
  'ua': {
    title: 'Safe Choice',
    expandBtn: 'Вподобання та алергії',
    medicalSection: 'Медична безпека',
    lifestyleSection: 'Спосіб життя та дієти',
    eDecoderBtn: 'Детектор E-добавок',
  },
  'en-US': {
    title: 'Safe Choice',
    expandBtn: 'Preferences & Allergies',
    medicalSection: 'Medical-Grade Safety',
    lifestyleSection: 'Lifestyle Preferences',
    eDecoderBtn: 'E-Decoder Inspector',
  }
};

export const SafeChoiceInlineConfig = ({
  safeChoiceProfile,
  handleSafeChoiceSave,
  lang = 'en-US',
  theme = 'dark',
  onOpenEDecoder
}: {
  safeChoiceProfile: SafeChoiceProfile;
  handleSafeChoiceSave: (newProfile: SafeChoiceProfile) => void;
  lang?: string;
  theme?: string;
  onOpenEDecoder?: () => void;
}) => {

  const [localProfile, setLocalProfile] = useState<SafeChoiceProfile>(safeChoiceProfile);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setLocalProfile(safeChoiceProfile);
  }, [safeChoiceProfile]);

  const currentLang = lang === 'ua' ? 'ua' : 'en-US';

  const toggleMedical = (allergy: string) => {
    const newProfile = {
      ...localProfile,
      medicalAllergies: localProfile.medicalAllergies.includes(allergy) 
        ? localProfile.medicalAllergies.filter(a => a !== allergy) 
        : [...localProfile.medicalAllergies, allergy]
    };
    setLocalProfile(newProfile);
    handleSafeChoiceSave(newProfile);
  };

  const toggleLifestyle = (pref: string) => {
    const newProfile = {
      ...localProfile,
      lifestylePreferences: localProfile.lifestylePreferences.includes(pref) 
        ? localProfile.lifestylePreferences.filter(p => p !== pref) 
        : [...localProfile.lifestylePreferences, pref]
    };
    setLocalProfile(newProfile);
    handleSafeChoiceSave(newProfile);
  };

  const toggleActive = () => {
    const newProfile = { ...localProfile, active: !localProfile.active };
    setLocalProfile(newProfile);
    handleSafeChoiceSave(newProfile);
  };

  return (
    <div className="flex flex-col gap-3 bg-[var(--card-bg)] p-4 rounded-2xl border border-[var(--card-border)]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={cn("p-2 rounded-lg", theme === 'light' ? "bg-zinc-50 text-zinc-500" : "bg-zinc-800 text-zinc-400")}>
            <AlertCircle className="w-4 h-4 text-emerald-500" />
          </div>
          <span className="text-sm font-bold uppercase tracking-widest text-zinc-700 dark:text-zinc-300">
            {UI_TEXTS[currentLang].title}
          </span>
        </div>
        <button
          type="button"
          onClick={toggleActive}
          className={cn(
            "w-12 h-6 rounded-full transition-colors relative",
            localProfile.active ? "bg-emerald-500" : "bg-zinc-300 dark:bg-zinc-700"
          )}
        >
          <div className={cn(
            "absolute top-1 w-4 h-4 bg-white rounded-full transition-all",
            localProfile.active ? "left-7" : "left-1"
          )} />
        </button>
      </div>

      {localProfile.active && (
        <>
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center justify-between w-full mt-2 pt-3 border-t border-[var(--card-border)] text-xs font-bold uppercase tracking-widest text-zinc-700 dark:text-zinc-400"
          >
            <span>{UI_TEXTS[currentLang].expandBtn}</span>
            <ChevronDown className={cn("w-4 h-4 transition-transform", isExpanded && "rotate-180")} />
          </button>
          
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="space-y-4 pt-4">
                  <div>
                    <h3 className="text-xs font-bold text-rose-500 flex items-center gap-2 mb-2 uppercase tracking-wider">
                      <AlertCircle className="w-3.5 h-3.5" />
                      {UI_TEXTS[currentLang].medicalSection}
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {['Peanuts', 'Tree Nuts', 'Dairy', 'Eggs', 'Gluten', 'Soy', 'Fish', 'Shellfish'].map(allergy => {
                        const label = ALLERGY_LABELS[currentLang][allergy] || allergy;
                        return (
                          <button
                            key={allergy}
                            onClick={() => toggleMedical(allergy)}
                            className={cn(
                              "px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors border",
                              localProfile.medicalAllergies.includes(allergy)
                                ? "bg-rose-500/10 border-rose-500/50 text-rose-500"
                                : "bg-[var(--input-bg)] border-[var(--card-border)] text-zinc-700 dark:text-zinc-400 hover:text-rose-400"
                            )}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xs font-bold text-emerald-500 flex items-center gap-2 mb-2 uppercase tracking-wider">
                      <Leaf className="w-3.5 h-3.5" />
                      {UI_TEXTS[currentLang].lifestyleSection}
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {['Vegan', 'Vegetarian', 'Keto', 'Paleo', 'Low-FODMAP', 'Clean Label'].map(pref => {
                        const label = PREFERENCE_LABELS[currentLang][pref] || pref;
                        return (
                          <button
                            key={pref}
                            onClick={() => toggleLifestyle(pref)}
                            className={cn(
                              "px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors border",
                              localProfile.lifestylePreferences.includes(pref)
                                ? "bg-emerald-500/10 border-emerald-500/50 text-emerald-500"
                                : "bg-[var(--input-bg)] border-[var(--card-border)] text-zinc-700 dark:text-zinc-400 hover:text-emerald-400"
                            )}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {onOpenEDecoder && (
                    <div className="pt-2 border-t border-[var(--card-border)]">
                      <button
                        type="button"
                        onClick={onOpenEDecoder}
                        className="w-full py-2.5 px-3 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-500 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-all cursor-pointer"
                      >
                        <ShieldAlert className="w-4 h-4" />
                        {UI_TEXTS[currentLang].eDecoderBtn}
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>

            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
};
